import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LayoutDashboard, Users, Shield, Map, AlertTriangle,
  ChevronLeft, ChevronRight, Settings, ChevronDown, RefreshCw, FileWarning, GitCompare, Building2, HeartPulse, BookOpen
} from 'lucide-react';
import { useUserStore } from '@/store/userStore';
import { useThemeStore } from '@/store/themeStore';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useQuery, useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { fetchSiniestAlertas, fetchSyncStatus, triggerSync } from '@/services/api';
import { useFiltersStore } from '@/store/filtersStore';


const NAV_ITEMS = [
  { to: '/', label: 'Dashboard', icon: LayoutDashboard, exact: true },
  { to: '/liderazgo', label: 'Liderazgo', icon: Users },
  { to: '/ops', label: 'OPS', icon: Shield },
  { to: '/near-misses', label: 'Near Misses', icon: AlertTriangle },
  { to: '/siniestralidad', label: 'Siniestralidad', icon: FileWarning, alertKey: true },
  { to: '/correlacion', label: 'Correlación', icon: GitCompare },
  { to: '/mapa', label: 'Mapa', icon: Map },
];

const ADMIN_ITEMS = [
  { to: '/admin', label: 'Administración', icon: Settings },
];

const HELP_ITEM = { to: '/ayuda', label: 'Manual / Ayuda', icon: BookOpen };

// Accidentes (Delt@) — visible solo para superadmin/admin + 3 emails autorizados
const ACCIDENTES_ALLOWED_EMAILS = [
  'dalonso@valorizasm.com',
  'lolivares@valorizasm.com',
  'abgarzon@valorizasm.com',
];
const ACCIDENTES_ITEM = { to: '/accidentes', label: 'Accidentes', icon: HeartPulse };

function NavItem({ item, collapsed, alertCount = 0 }) {
  return (
    <TooltipProvider delayDuration={0}>
      <Tooltip>
        <TooltipTrigger asChild>
          <NavLink
            to={item.to}
            end={item.exact}
            data-testid={`sidebar-nav-item-${item.label.toLowerCase().replace(/\s/g, '-').replace(/[^a-z0-9-]/g, '')}`}
            className="relative flex items-center gap-3 rounded-xl px-3 py-2.5 transition-all duration-200 group overflow-hidden"
          >
            {({ isActive }) => (
              <>
                {isActive && (
                  <motion.div
                    layoutId="nav-pill"
                    className="absolute inset-0 rounded-xl pointer-events-none"
                    style={{ background: 'linear-gradient(135deg, rgba(0,200,83,0.15), rgba(74,158,255,0.1))' }}
                    transition={{ type: 'spring', bounce: 0.2, duration: 0.4 }}
                  />
                )}
                {isActive && (
                  <div className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-5 rounded-r-full pointer-events-none" style={{ background: 'var(--vz-green)' }} />
                )}
                <div className="relative flex-shrink-0">
                  <item.icon
                    size={18}
                    className="relative z-10 transition-colors"
                    style={{ color: isActive ? 'var(--vz-green)' : 'rgba(255,255,255,0.75)' }}
                  />
                  {alertCount > 0 && (
                    <span
                      className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full flex items-center justify-center text-[8px] font-bold z-20"
                      style={{ background: '#EF5350', color: '#fff' }}
                      data-testid="sidebar-siniest-alert-badge"
                    >
                      {alertCount > 9 ? '9+' : alertCount}
                    </span>
                  )}
                </div>
                {!collapsed && (
                  <span
                    className="font-montserrat text-sm font-medium tracking-wide truncate relative z-10"
                    style={{ color: isActive ? '#FFFFFF' : 'rgba(255,255,255,0.78)' }}
                  >
                    {item.label}
                  </span>
                )}
                {!collapsed && alertCount > 0 && (
                  <span
                    className="ml-auto text-[9px] font-bold px-1.5 py-0.5 rounded-full z-10"
                    style={{ background: 'rgba(239,83,80,0.15)', color: '#EF5350', border: '1px solid rgba(239,83,80,0.3)' }}
                  >
                    {alertCount}
                  </span>
                )}
              </>
            )}
          </NavLink>
        </TooltipTrigger>
        {collapsed && (
          <TooltipContent side="right" className="bg-[#0F2045] border-[#1A3A6A] text-white">
            {item.label}{alertCount > 0 ? ` (${alertCount} alertas)` : ''}
          </TooltipContent>
        )}
      </Tooltip>
    </TooltipProvider>
  );
}

export default function Sidebar({ collapsed, onToggle }) {
  const { user } = useUserStore();
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';
  const navigate = useNavigate();
  const clearFilters = useFiltersStore(s => s.clearFilters);
  const canAdmin = user && ['superadmin', 'admin'].includes(user.role);
  const userEmail = (user?.email || '').trim().toLowerCase();
  const canAccidentes = canAdmin || ACCIDENTES_ALLOWED_EMAILS.includes(userEmail);
  const [syncOpen, setSyncOpen] = useState(false);

  const { data: syncStatus, refetch: refetchSync } = useQuery({
    queryKey: ['sync-status'],
    queryFn: fetchSyncStatus,
    refetchInterval: 30000,
    retry: false,
  });

  // Unread siniestralidad alerts count for badge
  const { data: alertas = [] } = useQuery({
    queryKey: ['siniest-alertas-count'],
    queryFn: fetchSiniestAlertas,
    refetchInterval: 60000,
    enabled: !!user,
    retry: false,
  });
  const alertCount = alertas.length;

  const syncMutation = useMutation({
    mutationFn: triggerSync,
    onSuccess: () => {
      toast.success('Sincronización iniciada', { description: 'Los datos de ArcGIS se están actualizando' });
      setTimeout(() => refetchSync(), 3000);
    },
    onError: () => toast.error('Error al iniciar la sincronización'),
  });

  const isSyncing = syncStatus?.status === 'running' || syncMutation.isPending;

  return (
    <motion.aside
      data-sidebar="true"
      data-dark-bg="true"
      animate={{ width: collapsed ? 64 : 220 }}
      transition={{ duration: 0.22, ease: 'easeInOut' }}
      className="relative flex flex-col h-full border-r overflow-hidden flex-shrink-0"
      style={{
        background: 'linear-gradient(180deg, #06152E 0%, #0B1A3E 100%)',
        borderColor: 'rgba(74,158,255,0.12)',
      }}
      data-testid="sidebar-nav"
    >
      {/* Logo */}
      <div className="flex items-center gap-3 px-3 pt-5 pb-4 border-b" style={{ borderColor: 'rgba(74,158,255,0.1)' }}>
        <div className="w-8 h-8 flex-shrink-0 flex items-center justify-center">
          <img
            src={isDark
              ? "https://customer-assets.emergentagent.com/job_926fe77a-4e51-48c9-adda-b35f91e2b951/artifacts/8zkzzvf3_VALORIZA_color_negativo%20%281%29.svg"
              : "/valoriza_positive.png"
            }
            alt="Valoriza"
            className="w-8 h-8 object-contain"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        </div>
        <AnimatePresence>
          {!collapsed && (
            <motion.div
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -8 }}
              transition={{ duration: 0.15 }}
              className="overflow-hidden min-w-0"
            >
              <p className="font-montserrat font-bold text-sm text-white leading-tight truncate">Valoriza</p>
              <p className="font-figtree text-[10px] truncate" style={{ color: 'rgba(170,204,255,0.55)' }}>OPS Dashboard</p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-2 py-4 space-y-0.5 overflow-y-auto overflow-x-hidden">
        {NAV_ITEMS.map((item) => (
          <NavItem
            key={item.to}
            item={item}
            collapsed={collapsed}
            alertCount={item.alertKey ? alertCount : 0}
          />
        ))}

        {canAccidentes && (
          <>
            <div className="my-3 mx-1" style={{ borderTop: '1px solid rgba(74,158,255,0.1)' }} />
            <NavItem item={ACCIDENTES_ITEM} collapsed={collapsed} />
          </>
        )}

        {canAdmin && (
          <>
            <div className="my-3 mx-1" style={{ borderTop: '1px solid rgba(74,158,255,0.1)' }} />
            {ADMIN_ITEMS.map((item) => (
              <NavItem key={item.to} item={item} collapsed={collapsed} />
            ))}
          </>
        )}
        {/* Manual / Ayuda — visible para todos los roles */}
        <div className="my-3 mx-1" style={{ borderTop: '1px solid rgba(74,158,255,0.1)' }} />
        <NavItem item={HELP_ITEM} collapsed={collapsed} />
      </nav>

      {/* Sync panel */}
      {!collapsed && canAdmin && (
        <div className="px-2 pb-3">
          <div className="rounded-xl overflow-hidden" style={{ background: 'rgba(0,200,83,0.05)', border: '1px solid rgba(0,200,83,0.15)' }}>
            <button
              onClick={() => setSyncOpen(!syncOpen)}
              className="w-full flex items-center justify-between px-3 py-2.5 text-xs hover:bg-[rgba(0,200,83,0.08)] transition-colors"
              style={{ color: 'rgba(200,220,255,0.9)' }}
            >
              <div className="flex items-center gap-2">
                <RefreshCw size={12} className={isSyncing ? 'animate-spin text-[var(--vz-green)]' : ''} style={{ color: isSyncing ? undefined : 'var(--vz-green)' }} />
                <span className="font-medium">Sincronización</span>
              </div>
              <ChevronDown size={12} className={`transition-transform ${syncOpen ? 'rotate-180' : ''}`} />
            </button>
            <AnimatePresence>
              {syncOpen && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden"
                >
                  <div className="px-3 pb-3 space-y-2">
                    <div className="text-[10px] space-y-1" style={{ color: 'rgba(170,204,255,0.75)' }}>
                      <div className="flex justify-between">
                        <span>Estado:</span>
                        <span className={syncStatus?.status === 'ok' ? 'text-[var(--vz-green)]' : syncStatus?.status === 'running' ? 'text-yellow-400' : 'text-[var(--vz-text-muted)]'}>
                          {syncStatus?.status || 'idle'}
                        </span>
                      </div>
                      {syncStatus?.last_sync && (
                        <div className="flex justify-between">
                          <span>Última:</span>
                          <span>{new Date(syncStatus.last_sync).toLocaleString('es-ES', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>
                        </div>
                      )}
                      {syncStatus?.ops_count > 0 && (
                        <div className="flex justify-between">
                          <span>OPS/Lid/NM:</span>
                          <span>{syncStatus.ops_count}/{syncStatus.liderazgo_count}/{syncStatus.nm_count}</span>
                        </div>
                      )}
                      <div className="text-[9px] pt-1" style={{ color: 'rgba(170,204,255,0.4)' }}>Auto: 07:00, 14:00, 21:00</div>
                    </div>
                    <button
                      onClick={() => syncMutation.mutate()}
                      disabled={isSyncing}
                      className="w-full flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-[10px] font-semibold transition-all disabled:opacity-50"
                      style={{ background: 'rgba(0,200,83,0.2)', color: 'var(--vz-green)', border: '1px solid rgba(0,200,83,0.3)' }}
                      data-testid="sidebar-sync-button"
                    >
                      <RefreshCw size={10} className={isSyncing ? 'animate-spin' : ''} />
                      {isSyncing ? 'Sincronizando...' : 'Sincronizar ahora'}
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      )}

      {/* Centros asignados — visible para usuarios con RLS */}
      {user && user.role !== 'superadmin' && user.role !== 'global_viewer' && Array.isArray(user.sedes) && user.sedes.length > 0 && (
        <TooltipProvider delayDuration={0}>
          <Tooltip>
            <TooltipTrigger asChild>
              <div
                className="mx-2 mb-1 rounded-xl px-3 py-2 cursor-default"
                style={{ background: 'rgba(74,158,255,0.06)', border: '1px solid rgba(74,158,255,0.15)' }}
                data-testid="sidebar-centros-asignados"
              >
                {collapsed ? (
                  <div className="flex justify-center">
                    <span className="text-xs font-bold" style={{ color: 'var(--vz-blue, #4A9EFF)' }}>{user.sedes.length}</span>
                  </div>
                ) : (
                  <div className="flex items-center justify-between">
                    <span className="text-[10px]" style={{ color: 'rgba(170,204,255,0.65)' }}>Centros asignados</span>
                    <span
                      className="text-xs font-bold px-2 py-0.5 rounded-full"
                      style={{ background: 'rgba(74,158,255,0.18)', color: '#4A9EFF' }}
                    >
                      {user.sedes.length}
                    </span>
                  </div>
                )}
              </div>
            </TooltipTrigger>
            <TooltipContent side="right" className="bg-[#0F2045] border-[#1A3A6A] text-white max-w-xs">
              <p className="text-xs font-semibold mb-1">{user.sedes.length} centros asignados:</p>
              <div className="text-[10px] space-y-0.5 max-h-40 overflow-y-auto" style={{ color: 'rgba(170,204,255,0.8)' }}>
                {user.sedes.map(s => <div key={s}>• {s}</div>)}
              </div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}

      {/* Ver mis centros button — reset filters to RLS default view */}
      {user && user.role !== 'superadmin' && user.role !== 'global_viewer' && Array.isArray(user.sedes) && user.sedes.length > 0 && (
        <TooltipProvider delayDuration={0}>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={() => { clearFilters(); navigate('/'); }}
                className="mx-2 mb-2 flex items-center gap-2 rounded-xl px-3 py-1.5 w-[calc(100%-1rem)] transition-colors"
                style={{ background: 'rgba(74,158,255,0.10)', border: '1px solid rgba(74,158,255,0.2)', color: '#4A9EFF' }}
                data-testid="sidebar-ver-mis-centros"
              >
                <Building2 size={13} className="flex-shrink-0" />
                {!collapsed && <span className="text-[11px] font-semibold">Ver mis centros</span>}
              </button>
            </TooltipTrigger>
            {collapsed && (
              <TooltipContent side="right" className="bg-[#0F2045] border-[#1A3A6A] text-white">
                <p className="text-xs">Ver mis centros</p>
              </TooltipContent>
            )}
          </Tooltip>
        </TooltipProvider>
      )}

      {/* Collapse toggle */}
      <button
        onClick={onToggle}
        data-testid="sidebar-collapse-button"
        className="absolute -right-3 top-[72px] w-6 h-6 rounded-full border flex items-center justify-center z-20 transition-all duration-200 hover:scale-110"
        style={{
          background: '#0B1A3E',
          borderColor: 'rgba(74,158,255,0.3)',
          color: 'rgba(170,204,255,0.8)',
          boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
        }}
      >
        {collapsed ? <ChevronRight size={12} /> : <ChevronLeft size={12} />}
      </button>
    </motion.aside>
  );
}
